import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LANGS } from "@/i18n";

export const Navbar = ({ t, lang, setLang, scrollTo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { hash: "#services", label: t.nav.services, id: "nav-services" },
    { hash: "#about", label: t.nav.about, id: "nav-about" },
    { hash: "#team", label: t.team.label, id: "nav-team" },
    { hash: "#why", label: t.nav.why, id: "nav-why" },
    { hash: "#contact", label: t.nav.contact, id: "nav-contact" },
  ];

  const go = (hash) => {
    setOpen(false);
    scrollTo(hash);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 border-b ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-[#0B132B]/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="flex items-center gap-3 group"
          aria-label="DKT Business Account Ab Oy"
        >
          <img src="/assets/logo-dark.png" alt="DKT Business Account logo" className="h-11 sm:h-14 w-auto object-contain" />
          <span className="hidden sm:block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 group-hover:text-[#8C713A] transition-colors">
            DKT Business Account Ab Oy
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
          {links.map((l) => (
            <button
              key={l.hash}
              data-testid={l.id}
              onClick={() => go(l.hash)}
              className="text-sm text-slate-600 hover:text-[#0B132B] transition-colors tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="flex items-center rounded-full border border-[#0B132B]/15 overflow-hidden" data-testid="language-switcher">
            {LANGS.map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={`px-3 py-1.5 text-xs font-mono2 uppercase tracking-widest transition-colors ${
                  lang === l ? "bg-[#0B132B] text-white" : "text-slate-500 hover:text-[#8C713A]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="nav-cta"
            onClick={() => go("#contact")}
            className="hidden md:inline-flex items-center rounded-full bg-[#0B132B] text-white text-sm font-medium px-5 py-2.5 hover:bg-[#C5A059] hover:text-[#0B132B] transition-colors"
          >
            {t.nav.cta}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-slate-700 p-2"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-[#0B132B]/10" data-testid="nav-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <button
                key={l.hash}
                data-testid={`${l.id}-mobile`}
                onClick={() => go(l.hash)}
                className="text-left text-lg font-display text-slate-700 hover:text-[#8C713A] transition-colors"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
